import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, X, Cube, Calendar, PaperPlaneTilt } from "@phosphor-icons/react";

function fmtK(n) {
  return `K ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CATEGORIES = [
  { k: "plant", label: "Plant & Machinery", asset_gl: "1510", accum_gl: "1590" },
  { k: "vehicle", label: "Motor Vehicles",    asset_gl: "1520", accum_gl: "1590" },
  { k: "other",   label: "Other",             asset_gl: "1500", accum_gl: "1590" },
];

const BLANK = {
  code: "", name: "", category: "plant",
  purchase_date: new Date().toISOString().slice(0,10),
  purchase_cost: "", salvage_value: 0, useful_life_months: 60,
  asset_gl_code: "1510", accum_dep_gl_code: "1590", dep_exp_gl_code: "5400",
};

export default function FixedAssets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [runMonth, setRunMonth] = useState(() => new Date().toISOString().slice(0,7));
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/fa/assets");
      setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!form.code || !form.name || !form.purchase_cost) return toast.error("Code, name, cost required");
    setBusy(true);
    try {
      await api.post("/fa/assets", {
        ...form,
        purchase_cost: Number(form.purchase_cost),
        salvage_value: Number(form.salvage_value || 0),
        useful_life_months: Number(form.useful_life_months),
      });
      toast.success("Fixed asset created");
      setOpen(false); setForm(BLANK);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  async function runDepreciation() {
    if (!/^\d{4}-\d{2}$/.test(runMonth)) return toast.error("Enter YYYY-MM");
    if (!window.confirm(`Post depreciation journals for ${runMonth}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/fa/run-month", { month: runMonth });
      toast.success(`Posted ${data.posted}, skipped ${data.skipped} of ${data.asset_count}`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  const totalCost = rows.reduce((s, a) => s + a.purchase_cost, 0);
  const totalNBV  = rows.reduce((s, a) => s + a.book_value, 0);
  const totalAccum = rows.reduce((s, a) => s + a.accumulated_depreciation, 0);

  return (
    <div data-testid="fa-page">
      <PageHeader
        overline="Finance / Fixed Assets"
        title="Fixed Asset Register"
        subtitle="Straight-line depreciation to the GL — book values kept live."
        testId="fa-header"
      />

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="phizo-card p-4">
          <div className="phizo-label text-[10px]">Assets</div>
          <div className="mono text-3xl font-black">{rows.length}</div>
        </div>
        <div className="phizo-card p-4">
          <div className="phizo-label text-[10px]">Original cost</div>
          <div className="mono text-2xl font-black">{fmtK(totalCost)}</div>
        </div>
        <div className="phizo-card p-4">
          <div className="phizo-label text-[10px]">Net book value</div>
          <div className="mono text-2xl font-black" style={{ color: "#059669" }}>{fmtK(totalNBV)}</div>
          <div className="text-[10px] text-[#71717a]">Accum. dep {fmtK(totalAccum)}</div>
        </div>
      </div>

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Calendar size={16} weight="bold" className="text-[#71717a]" />
        <input value={runMonth} onChange={(e) => setRunMonth(e.target.value)}
               placeholder="YYYY-MM"
               className="border border-[#e4e4e7] px-3 py-1.5 text-xs mono outline-none focus:border-black w-28"
               data-testid="fa-month-input"/>
        <button onClick={runDepreciation} disabled={busy} data-testid="fa-run-btn"
                className="border border-black bg-black text-white px-4 py-1.5 text-xs hover:bg-[#27272a] disabled:opacity-60 flex items-center gap-1.5">
          <PaperPlaneTilt size={12} weight="bold" /> Run monthly depreciation
        </button>
        <button onClick={async () => {
                  try {
                    const { data } = await api.get("/fa/candidate-machines");
                    setCandidates(data.rows || []);
                    setCandidatesOpen(true);
                  } catch (e) { toast.error(formatError(e)); }
                }}
                data-testid="fa-from-machine-btn"
                className="ml-auto border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5] flex items-center gap-1.5">
          <Cube size={12} weight="bold" /> Convert machine / truck
        </button>
        <button onClick={() => { setForm(BLANK); setOpen(true); }}
                data-testid="fa-new-btn"
                className="bg-[#002FA7] text-white px-4 py-1.5 text-xs hover:bg-[#001f7a] flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> Register asset
        </button>
      </div>

      <div className="phizo-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">Code</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Name</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Category</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Cost</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Mo. dep</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Accum</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">NBV</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Last run</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#71717a]">Register your first fixed asset above.</td></tr>
            )}
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                  data-testid={`fa-row-${a.code}`}>
                <td className="px-3 py-2 mono font-bold">{a.code}</td>
                <td className="px-3 py-2 font-semibold">{a.name}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase text-[#71717a] tracking-wider">{a.category}</span></td>
                <td className="px-3 py-2 text-right mono">{fmtK(a.purchase_cost)}</td>
                <td className="px-3 py-2 text-right mono">{fmtK(a.monthly_depreciation)}</td>
                <td className="px-3 py-2 text-right mono text-[#991b1b]">{fmtK(a.accumulated_depreciation)}</td>
                <td className="px-3 py-2 text-right mono font-bold" style={{ color: "#059669" }}>{fmtK(a.book_value)}</td>
                <td className="px-3 py-2 mono text-[11px] text-[#71717a]">{a.last_depreciated_month || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="fa-modal">
            <div className="px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Fixed assets</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">Register asset</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-[#71717a]"><X size={16} weight="bold"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Asset code</div>
                  <input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"
                         data-testid="fa-form-code"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Category</div>
                  <select value={form.category} onChange={(e) => {
                    const cat = CATEGORIES.find((c) => c.k === e.target.value);
                    setForm({...form, category: e.target.value,
                             asset_gl_code: cat?.asset_gl || "1510",
                             accum_dep_gl_code: cat?.accum_gl || "1590"});
                  }}
                    className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black">
                    {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                  </select>
                </label>
                <label className="text-xs col-span-2">
                  <div className="phizo-label text-[10px] mb-1">Name</div>
                  <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="fa-form-name"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Purchase date</div>
                  <input type="date" value={form.purchase_date}
                         onChange={(e) => setForm({...form, purchase_date: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Purchase cost (ZMW)</div>
                  <input type="number" step="0.01" value={form.purchase_cost}
                         onChange={(e) => setForm({...form, purchase_cost: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"
                         data-testid="fa-form-cost"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Salvage value</div>
                  <input type="number" step="0.01" value={form.salvage_value}
                         onChange={(e) => setForm({...form, salvage_value: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Useful life (months)</div>
                  <input type="number" value={form.useful_life_months}
                         onChange={(e) => setForm({...form, useful_life_months: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                </label>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">Cancel</button>
              <button onClick={create} disabled={busy} data-testid="fa-save-btn"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs hover:bg-[#001f7a] disabled:opacity-60">
                {busy ? "Saving…" : "Register"}
              </button>
            </div>
          </div>
        </div>
      )}
      {candidatesOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setCandidatesOpen(false)}>
          <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fa-candidates-modal">
            <div className="px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Fixed assets</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">Convert existing machine / truck</h3>
                <div className="text-[11px] text-[#71717a] mt-1">
                  Uses each item's own purchase cost, useful life and salvage value.
                  Items without a purchase cost recorded are greyed out.
                </div>
              </div>
              <button onClick={() => setCandidatesOpen(false)} className="p-1 text-[#71717a]"><X size={16} weight="bold"/></button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                <tr className="text-left">
                  <th className="px-3 py-2 phizo-label text-[9px]">Kind</th>
                  <th className="px-3 py-2 phizo-label text-[9px]">Name</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Cost</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-[#71717a]">
                    All machines & trucks are already registered as fixed assets.
                  </td></tr>
                )}
                {candidates.map((c) => (
                  <tr key={`${c.kind}-${c.id}`} className={`border-b border-[#f4f4f5] ${!c.purchase_cost ? "opacity-50" : ""}`}
                      data-testid={`fa-candidate-${c.asset_tag || c.id.slice(0,6)}`}>
                    <td className="px-3 py-2 mono text-[10px] uppercase text-[#71717a]">{c.kind}</td>
                    <td className="px-3 py-2 font-semibold">{c.name}
                      <div className="mono text-[10px] text-[#71717a]">{c.asset_tag}</div>
                    </td>
                    <td className="px-3 py-2 text-right mono">{c.purchase_cost ? fmtK(c.purchase_cost) : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/fa/from-${c.kind}/${c.id}`);
                            toast.success(`${c.name} registered`);
                            setCandidatesOpen(false);
                            await load();
                          } catch (e) { toast.error(formatError(e)); }
                        }}
                        disabled={!c.purchase_cost}
                        data-testid={`fa-convert-${c.asset_tag || c.id.slice(0,6)}`}
                        className="border border-black bg-black text-white px-3 py-1 text-[11px] hover:bg-[#27272a] disabled:opacity-30 disabled:cursor-not-allowed">
                        Register
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
